/**
 * Value formatting.
 *
 * Deliberately hand-rolled rather than `Intl.NumberFormat` / `toLocaleString`.
 * Node's number formatting depends on which ICU build it was compiled against —
 * a small-icu runtime and a full-icu runtime can group and space digits differently
 * for the same input. R4 requires byte-identical PDFs across runs and machines, so
 * formatting must not depend on the host's locale data at all.
 */

/** 1114255 -> "1,114,255" */
export function formatInt(n: number): string {
  const neg = n < 0;
  const digits = Math.abs(Math.round(n)).toString();
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ',';
    out += digits[i];
  }
  return (neg ? '-' : '') + out;
}

/** 1.0343 -> "1.03" — a ratio that sits near 1 (CTV average frequency), where an integer would read "1". */
export function formatDec2(n: number): string {
  return n.toFixed(2);
}

/** 21.7243 -> "21.72%" */
export function formatPct(n: number, decimals = 2): string {
  return `${n.toFixed(decimals)}%`;
}

/**
 * How a block renders a value that isn't available.
 *
 * Pre-flight has no delivery, and the data model's convention is that an unknown
 * metric is `null` while a measured zero is `0`. Conflating them is how a pre-flight
 * report ends up claiming zero impressions, so the two paths are kept apart here and
 * the choice belongs to the manifest, not the template.
 */
export type Unavailable = 'tbd' | 'zero' | 'hide';

export function renderMetric(
  value: unknown,
  unavailable: Unavailable,
  format: (n: number) => string = formatInt
): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return format(value);

  /*
   * A non-finite number renders as 0, never as the text "NaN".
   *
   * This was a live bug here: `NaN` is `typeof 'number'` but fails `Number.isFinite`,
   * so it fell past both branches to `String(value)` and would have printed the
   * literal string "NaN" onto a client report — exactly what the legacy system does on
   * 22.7% of issued reports (docs/09-track-a-findings.md §1).
   *
   * NaN is a BROKEN number, not an absent one. That distinction is why it is handled
   * here rather than folded into the null branch below: "we computed something and it
   * came out invalid" is a different statement from "this is not knowable yet", and
   * only the second deserves TBD. Showing 0 for a fault is a presentation decision;
   * the fault itself must still surface through provenance, not be laundered by it.
   */
  if (typeof value === 'number') return format(0);

  if (value === null || value === undefined) {
    if (unavailable === 'zero') return format(0);
    if (unavailable === 'hide') return null;
    return 'TBD';
  }
  return String(value);
}
